// Offers panel on the menu editor (slice 20).
//
// Owner creates offers (percent / rupees off / fixed price / words only, ADR 0100);
// the pictures at the top of the menu are a separate panel (BannersPanel).
// A list below is grouped by state (scheduled / active / expired /
// cancelled). Each non-terminal row has a Cancel button.
//
// The server is authoritative on every field validation; this file
// does the minimum to help the owner (required markers, scope-conditional
// fields, Date-roundtrip for the datetime-local input). Permission
// gating lives in the parent — this component takes `canEdit` and
// hides the forms + Cancel button accordingly.

import { useMemo, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import { DiscountOfferForm, describeDiscount } from './DiscountOfferForm.js';
import type {
  CreateDiscountOfferBody,
  CreateOfferBody,
  CreateOfferResponse,
  ListOffersResponse,
  MenuDetail,
  Offer,
  OfferState,
} from '../../api/types.js';

interface OffersPanelProps {
  readonly orgId: string;
  readonly menuId: string;
  readonly menu: MenuDetail;
  readonly canEdit: boolean;
}

const DEFAULT_LANG = 'en';

const GROUP_ORDER: readonly OfferState[] = ['active', 'scheduled', 'expired', 'cancelled'];
const GROUP_LABEL: Record<OfferState, string> = {
  active: 'Active now',
  scheduled: 'Scheduled',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export function OffersPanel(props: OffersPanelProps): JSX.Element {
  const { orgId, menuId, menu, canEdit } = props;
  const qc = useQueryClient();
  const listKey = ['offers', orgId, menuId] as const;

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => apiFetch<ListOffersResponse>(`/organizations/${orgId}/menus/${menuId}/offers`),
    // Scheduled → active transitions happen on the server's cron (every
    // minute). A refetch keeps the admin's view roughly in step without
    // being chatty.
    refetchInterval: 30_000,
  });

  const create = useMutation<CreateOfferResponse, Error, CreateOfferBody>({
    mutationFn: (body) =>
      apiFetch<CreateOfferResponse>(`/organizations/${orgId}/menus/${menuId}/offers`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
  });

  const cancel = useMutation<void, Error, string>({
    mutationFn: (offerId) =>
      apiFetch<void>(`/organizations/${orgId}/offers/${offerId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
  });

  const grouped = useMemo(() => groupByState(list.data?.offers ?? []), [list.data]);

  return (
    <details className="card offers-panel panel-fold">
      <summary>
        <h2 id="offers-heading">Offers</h2>
      </summary>
      <p className="muted">
        An offer puts words on a dish’s picture and, when it takes money off, shows the old price
        struck through beside the new one. It shows on the menu page only; the till does not apply
        it. Scheduled offers start and end by themselves.
      </p>

      {canEdit ? (
        <>
          <DiscountOfferForm
            menu={menu}
            create={{
              mutate: (body: CreateDiscountOfferBody, opts) => create.mutate(body, opts),
              isPending: create.isPending,
              error: create.error,
            }}
          />
        </>
      ) : null}

      {list.isLoading ? <p className="muted">Loading offers…</p> : null}
      {list.error ? (
        <p role="alert" className="error">
          {list.error instanceof ApiError ? list.error.message : 'could not load offers'}
        </p>
      ) : null}

      {list.data && list.data.offers.length === 0 ? (
        <p className="muted">
          {canEdit
            ? 'No offers yet. Create one above to run a festival banner or a happy-hour price.'
            : 'No offers on this menu yet.'}
        </p>
      ) : null}

      {GROUP_ORDER.map((state) => {
        const rows = grouped[state];
        if (!rows || rows.length === 0) return null;
        return (
          <div key={state} className="offer-group">
            <h3>
              <span className={`chip chip-${state}`}>{GROUP_LABEL[state]}</span>{' '}
              <span className="muted">({rows.length})</span>
            </h3>
            <ul className="offer-list">
              {rows.map((o) => (
                <OfferRow
                  key={o.id}
                  offer={o}
                  menu={menu}
                  canEdit={canEdit}
                  onCancel={() => cancel.mutate(o.id)}
                  cancelPending={cancel.isPending}
                />
              ))}
            </ul>
          </div>
        );
      })}

      {cancel.error ? (
        <p role="alert" className="error">
          {cancel.error instanceof ApiError ? cancel.error.message : 'could not cancel'}
        </p>
      ) : null}
    </details>
  );
}

function OfferRow(props: {
  readonly offer: Offer;
  readonly menu: MenuDetail;
  readonly canEdit: boolean;
  readonly onCancel: () => void;
  readonly cancelPending: boolean;
}): JSX.Element {
  const { offer, menu, canEdit, onCancel, cancelPending } = props;
  const subtitle =
    offer.scope === 'menu_banner'
      ? offer.bannerText
      : offer.scope === 'discount'
        ? describeCover(offer, menu)
        : describeItemOverride(offer, menu);
  const window = formatWindow(offer);
  const canCancel = offer.state === 'scheduled' || offer.state === 'active';

  return (
    <li className="offer-row">
      <div>
        <strong>
          {offer.scope === 'menu_banner'
            ? 'Banner'
            : offer.scope === 'discount'
              ? describeDiscount(offer) || 'Offer'
              : 'Item override (old style)'}
        </strong>
        <br />
        <span className="muted">{subtitle}</span>
        <br />
        <span className="muted">{window}</span>
      </div>
      {canEdit && canCancel ? (
        <button type="button" onClick={onCancel} disabled={cancelPending}>
          {cancelPending ? 'Cancelling…' : 'Cancel'}
        </button>
      ) : null}
    </li>
  );
}

function describeCover(offer: Offer, menu: MenuDetail): string {
  if (offer.scope !== 'discount') return '';
  const t = offer.target;
  if (t.kind === 'all') return 'Every dish';
  if (t.kind === 'category') {
    const c = menu.categories.find((x) => x.id === t.categoryId);
    const label =
      c?.translations.find((x) => x.languageCode === DEFAULT_LANG)?.name ??
      c?.translations[0]?.name ??
      '(unknown category)';
    return `Category: ${label}`;
  }
  const names = t.itemIds.map((id) => findItemName(menu, id) ?? '(unknown dish)');
  return `${names.length === 1 ? 'Dish' : `${names.length} dishes`}: ${names.join(', ')}`;
}

function describeItemOverride(offer: Offer, menu: MenuDetail): string {
  if (offer.scope !== 'item_override') return '';
  const name = findItemName(menu, offer.menuItemId) ?? '(unknown item)';
  const parts: string[] = [name];
  if (offer.pricePaiseOverride !== undefined) {
    parts.push(`₹${(offer.pricePaiseOverride / 100).toFixed(2)}`);
  }
  if (offer.badgeText) {
    parts.push(`“${offer.badgeText}”`);
  }
  return parts.join(' · ');
}

function findItemName(menu: MenuDetail, itemId: string): string | null {
  for (const c of menu.categories) {
    for (const i of c.items) {
      if (i.id === itemId) {
        const t = i.translations.find((x) => x.languageCode === DEFAULT_LANG) ?? i.translations[0];
        return t?.name ?? null;
      }
    }
  }
  return null;
}

function groupByState(offers: readonly Offer[]): Partial<Record<OfferState, readonly Offer[]>> {
  const out: Partial<Record<OfferState, Offer[]>> = {};
  for (const o of offers) {
    (out[o.state] ??= []).push(o);
  }
  return out;
}

function formatWindow(offer: Offer): string {
  const start = new Date(offer.activatesAt);
  const end = new Date(offer.expiresAt);
  const now = Date.now();
  const fmt = (d: Date): string =>
    d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  const base = `${fmt(start)} → ${fmt(end)}`;
  if (offer.state === 'scheduled') {
    const mins = Math.max(0, Math.round((start.getTime() - now) / 60_000));
    return `${base} · starts in ${formatMinutes(mins)}`;
  }
  if (offer.state === 'active') {
    const mins = Math.max(0, Math.round((end.getTime() - now) / 60_000));
    return `${base} · ends in ${formatMinutes(mins)}`;
  }
  return base;
}

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours < 24) return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  const days = Math.floor(hours / 24);
  const hrem = hours % 24;
  return hrem === 0 ? `${days}d` : `${days}d ${hrem}h`;
}
